import { localizePath, splitLocale } from '@/lib/i18n/paths'
import { DEFAULT_LOCALE } from '@/lib/i18n/config'
import { currentLocale, localeHeaders } from '@/lib/i18n/request'
import { getMessages } from '@/lib/i18n/messages'
import messages from './api-client.i18n'
import { useAuthStore } from '@/lib/store/auth-store'

export const API_URL = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001/api'

// Los mensajes de class-validator vienen en inglés y exponen nombres internos
// de campos (ej. "recurrenceConfig.endDate must be a Date instance") — no son
// aptos para mostrarle al usuario final. Si el mensaje calza con ese patrón,
// se reemplaza por uno genérico en el idioma de la página.
const RAW_VALIDATION_PATTERN = /^[a-zA-Z0-9_.[\]]+\s+(must|should|is not|cannot)\b/

export function friendlyApiMessage(
  message: string,
  fallback = getMessages(messages, currentLocale()).validationFallback,
): string {
  return RAW_VALIDATION_PATTERN.test(message) ? fallback : message
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function apiClient<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      // Idioma de la página: el backend traduce sus mensajes de error.
      ...localeHeaders(),
      ...options?.headers,
    },
  })

  if (!res.ok) {
    let message = `HTTP ${res.status}`
    let code: string | undefined
    try {
      const body = await res.json()
      message = body.message ?? message
      code = body.code
    } catch {
      // ignore parse error
    }

    // El token venció más allá de la ventana de gracia (ver JwtAuthGuard en
    // el backend, que renueva solo dentro de esa ventana) — a diferencia de
    // otros 401/403, acá no hay nada que reintentar: hay que volver a loguearse.
    if (code === 'SESSION_EXPIRED') {
      useAuthStore.getState().clearUser()
      if (typeof window !== 'undefined') {
        // Mismo idioma de la página actual (las URLs llevan prefijo /es, /en).
        const lang = splitLocale(window.location.pathname).lang ?? DEFAULT_LOCALE
        window.location.href = localizePath('/login?session=expired', lang)
      }
    }

    throw new ApiError(res.status, message, code)
  }

  // Handle 204 No Content
  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => apiClient<T>(path),
  post: <T>(path: string, body: unknown) =>
    apiClient<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    }),
  patch: <T>(path: string, body: unknown) =>
    apiClient<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    }),
  delete: <T>(path: string) => apiClient<T>(path, { method: 'DELETE' }),
}
